import React, { useContext, useState } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import {
  Table,
  TableRow,
  TableBody,
  Box,
  Typography,
  useMediaQuery,
  Chip,
  Slide,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import { UserContext } from '../../lib/contexts/UserContext';
import MobileNutrientRow from './components/MobileNutrientRow';
import NutrientRow from './components/NutrientRow';
import NutrientTableHeader from './components/NutrientTableHeader';
import MacronutrientRow from './components/MacronutrientRow';
import MobileMacronutrientRow from './components/MobileMacronutrientRow';
import MacronutrientHeader from './components/MacronutrientHeader';
import { getWorkflowToken } from '../../lib/helpers/helpers';
import { calculateNutrientScores, calculateNutrientChange } from './utils/nutrientCalculations';
import useNutrients from '../../lib/hooks/useNutrients';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: '18.72px',
    fontWeight: 700,
    lineHeight: '116.7%',
    textTransform: 'capitalize',
    color: theme.palette.black87,
    marginBottom: '16px',
  },
  mobileLayout: {
    display: 'flex',
    flexDirection: 'column',
    width: '313px',
    gap: '8px',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    maxWidth: '806px',
    width: '806px',
    tableLayout: 'fixed',
    borderRadius: '8px',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      maxWidth: '313px',
      width: '313px',
    },
  },
  unitBold: {
    fontWeight: 600,
    margin: 0,
  },
  unitRegular: {
    fontWeight: 400,
  },
  mobileTitleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  mobileTitle: {
    fontSize: '18.72px',
    fontWeight: 700,
    lineHeight: '116.7%',
    textTransform: 'capitalize',
    color: theme.palette.black87,
    margin: 0,
  },
}));

// TBD put colors in light.js
// warning/dark color #E65100
// warning/_states/selected #EF6C00 8%
// success/main color #1B5E20
// success/_states/selected #2E7D32 8%
export const getNutrientChange = (type, current, goal, unit, classes) => {
  const { value } = calculateNutrientChange(current, goal, unit);

  if (!value && value !== 0) {
    return null;
  }

  if (value === 0 || type?.trim() === 'fats' || type?.trim() === 'macronutrients') {
    return (
      <p className={classes.unitBold}>
        {value} <span className={classes.unitRegular}>{unit}</span>
      </p>
    );
  }

  return (
    <Chip
      label={
        <>
          <span className={classes.unitBold}>{value}</span>
          <span className={classes.unitRegular}>&nbsp;{unit}</span>
        </>
      }
      sx={{
        backgroundColor: value < 0 ? '#EF6C0014' : '#2E7D3214',
        color: value < 0 ? '#E65100' : '#1B5E20',
        height: 24,
        '& .MuiChip-label': {
          padding: '0 8px',
        },
      }}
    />
  );
};

const WorkflowBox = ({ type = 'nutrients', mockData = null }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentView, setCurrentView] = useState('current');
  const [expanded, setExpanded] = useState(false);
  const {
    state: { userObject },
  } = useContext(UserContext);

  const workflowToken = getWorkflowToken(userObject?.workflowShareUrl);

  const { data: dataArray, isLoading: dataArrayLoading } = useNutrients(workflowToken, type);

  const formatMacronutrientData = (data) => {
    if (!data || !Array.isArray(data)) return null;

    // Get values from data array and ensure they are valid numbers
    const currentCarbs = Number(data[1]?.current) || 0;
    const currentFat = Number(data[2]?.current) || 0;
    const currentProtein = Number(data[3]?.current) || 0;

    // Calculate total calories from macronutrients
    const totalCalories = currentCarbs * 4 + currentFat * 9 + currentProtein * 4;

    // Calculate current percentages for the pie chart
    const currentPercentages = {
      carbohydrates: totalCalories > 0 ? Math.round(((currentCarbs * 4) / totalCalories) * 100) : 0,
      totalFat: totalCalories > 0 ? Math.round(((currentFat * 9) / totalCalories) * 100) : 0,
      protein: totalCalories > 0 ? Math.round(((currentProtein * 4) / totalCalories) * 100) : 0,
    };

    const macroData = {
      calories: {
        name: data[0]?.name || 'Estimated Calorie Intake',
        current: data[0]?.current || '0',
        goal: data[0]?.goal || '0',
        unit: data[0]?.unit || 'kcal',
      },
      macronutrients: [
        {
          name: data[1]?.name || 'Carbohydrates',
          current: data[1]?.current || '0',
          currentPercentage: currentPercentages.carbohydrates || null,
          goal: data[1]?.goal || '0',
          goalPercentage:
            totalCalories > 0 ? Math.round(((Number(data[1]?.goal || 0) * 4) / totalCalories) * 100) : null,
          unit: data[1]?.unit || 'g',
          allCurrentPercentages: currentPercentages,
        },
        {
          name: data[2]?.name || 'Total Fat',
          current: data[2]?.current || '0',
          currentPercentage: currentPercentages.totalFat || null,
          goal: data[2]?.goal || '0',
          goalPercentage:
            totalCalories > 0 ? Math.round(((Number(data[2]?.goal || 0) * 9) / totalCalories) * 100) : null,
          unit: data[2]?.unit || 'g',
          allCurrentPercentages: currentPercentages,
        },
        {
          name: data[3]?.name || 'Protein',
          current: data[3]?.current || '0',
          currentPercentage: currentPercentages.protein || null,
          goal: data[3]?.goal || '0',
          goalPercentage:
            totalCalories > 0 ? Math.round(((Number(data[3]?.goal || 0) * 4) / totalCalories) * 100) : null,
          unit: data[3]?.unit || 'g',
          allCurrentPercentages: currentPercentages,
        },
      ],
    };

    return {
      ...macroData,
      calories: {
        ...macroData.calories,
        calculatedChange: null,
      },
      macronutrients: macroData.macronutrients.map((nutrient) => ({
        ...nutrient,
        calculatedChange: getNutrientChange(
          'macronutrients',
          Number(nutrient.current),
          Number(nutrient.goal),
          nutrient.unit,
          classes,
        ),
      })),
    };
  };

  const getNutrientData = ({ type, item, index }) => {
    const scores = calculateNutrientScores(item);
    const calculatedChange = getNutrientChange(type, Number(item.current), Number(item.goal), item.unit, classes);
    const longDescription = item?.description;

    return {
      ...scores,
      calculatedChange,
      longDescription,
    };
  };

  if (isMobile) {
    return (
      <Box className={classes.mobileLayout}>
        {type !== 'macronutrients' && (
          <Box className={classes.mobileTitleContainer}>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="medium"
              sx={{ padding: '4px' }}
            >
              {expanded ? (
                <KeyboardArrowDown sx={{ height: '32px', width: '32px' }} />
              ) : (
                <KeyboardArrowRight sx={{ height: '32px', width: '32px' }} />
              )}
            </IconButton>
            <Typography className={classes.mobileTitle}>{type}</Typography>
          </Box>
        )}
        <Collapse in={expanded || type === 'macronutrients'}>
          <Table className={classes.patientChartCard}>
            {type === 'macronutrients' ? (
              <MacronutrientHeader
                variant="mobile"
                onViewChange={setCurrentView}
                currentView={currentView}
              />
            ) : (
              <NutrientTableHeader variant="mobile" />
            )}
            <TableBody>
              {type === 'macronutrients' ? (
                <>
                  <MobileMacronutrientRow
                    name={formatMacronutrientData(dataArray)?.calories.name}
                    current={formatMacronutrientData(dataArray)?.calories.current}
                    goal={formatMacronutrientData(dataArray)?.calories.goal}
                    unit={formatMacronutrientData(dataArray)?.calories.unit}
                    calculatedChange={formatMacronutrientData(dataArray)?.calories.calculatedChange}
                    isCalorieRow
                    currentView={currentView}
                  />
                  {formatMacronutrientData(dataArray)?.macronutrients.map((nutrient, index, array) => (
                    <MobileMacronutrientRow
                      key={index}
                      name={nutrient.name}
                      current={nutrient.current}
                      currentPercentage={nutrient.currentPercentage}
                      goal={nutrient.goal}
                      goalPercentage={nutrient.goalPercentage}
                      unit={nutrient.unit}
                      calculatedChange={nutrient.calculatedChange}
                      allCurrentPercentages={nutrient.allCurrentPercentages}
                      currentView={currentView}
                      isLastRow={index === array.length - 1}
                    />
                  ))}
                </>
              ) : (
                dataArray.map((item, index) => (
                  <MobileNutrientRow
                    key={index}
                    item={item}
                    {...getNutrientData({ type, item, index })}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Collapse>
      </Box>
    );
  }

  return (
    <Table className={classes.patientChartCard}>
      {type === 'macronutrients' ? (
        <MacronutrientHeader
          variant="desktop"
          type={type}
        />
      ) : (
        <NutrientTableHeader
          variant="desktop"
          type={type}
          onExpandChange={setExpanded}
        />
      )}
      <Collapse in={expanded || type === 'macronutrients'}>
        <TableBody>
          {type === 'macronutrients' ? (
            <>
              <MacronutrientRow
                calories={formatMacronutrientData(dataArray)?.calories}
                isCalorieRow
              />
              <MacronutrientRow macronutrients={formatMacronutrientData(dataArray)?.macronutrients} />
            </>
          ) : (
            dataArray.map((item, index) => (
              <NutrientRow
                type={type}
                key={index}
                item={item}
                {...getNutrientData({ type, item, index })}
              />
            ))
          )}
        </TableBody>
      </Collapse>
    </Table>
  );
};

export default WorkflowBox;
